/**
 * A client's history on one movement, session by session, out of the records read's recent sets.
 *
 * The read is flat — one row per counted set, newest first — and every row names the workout
 * execution it was lifted in, so a session is the rows sharing that id. Warm-ups never arrive: the
 * backend excludes them, which is why nothing here filters by set type.
 *
 * The e1RM is the value the backend cached when the session was finalised. It is never recomputed
 * here: a set whose cached value is empty contributes nothing to the session's e1RM rather than a
 * figure this app invented.
 */

import type { LiftLine } from './liftLines';

/** One counted set as the read carries it, before grouping. */
export interface LiftSetSource {
  readonly workoutExecutionId: number | null;
  readonly setId: number | null;
  readonly weightKg: number | null;
  readonly reps: number | null;
  readonly e1rmKg: number | null;
  /** ISO instant the session was finished at. */
  readonly achievedAt: string | null;
  /** Absent from a read that predates timed, distance, round and speed sets. */
  readonly durationSeconds?: number | null;
  readonly distanceMeters?: number | null;
  readonly rounds?: number | null;
  readonly speedKmh?: number | null;
}

export interface LiftSet {
  readonly setId: number;
  readonly weightKg: number | null;
  readonly reps: number | null;
  readonly e1rmKg: number | null;
  readonly durationSeconds: number | null;
  readonly distanceMeters: number | null;
  readonly rounds: number | null;
  readonly speedKmh: number | null;
}

export interface LiftSession {
  readonly workoutExecutionId: number;
  /** When the session was finished, as epoch milliseconds. */
  readonly performedAt: number;
  /** In the order they were lifted. */
  readonly sets: readonly LiftSet[];
  /** The session's highest cached e1RM, or `null` when no set carries one. */
  readonly e1rmKg: number | null;
  /** The heaviest set's weight. */
  readonly topWeightKg: number | null;
  /** Every repetition of the session; `null` when no set counted repetitions (a timed set). */
  readonly reps: number | null;
  /** Σ weight × reps, in kilograms; `null` when no set carries both (bodyweight, timed). */
  readonly volumeKg: number | null;
}

const finite = (value: number | null): value is number => value !== null && Number.isFinite(value);

function maxOf(values: readonly (number | null)[]): number | null {
  const present = values.filter(finite);
  return present.length === 0 ? null : Math.max(...present);
}

function setVolumeKg(set: LiftSet): number | null {
  return finite(set.weightKg) && finite(set.reps) ? set.weightKg * set.reps : null;
}

/** A set with no figure adds nothing, and a session where no set has one has no figure at all. */
function sumOf(values: readonly (number | null)[]): number | null {
  const present = values.filter(finite);
  return present.length === 0 ? null : present.reduce((sum, value) => sum + value, 0);
}

/** Seconds per kilometre over the sets that carry both a time and a distance. */
function paceOf(sets: readonly LiftSet[]): number | null {
  const timed = sets.filter(
    (set) => finite(set.durationSeconds) && finite(set.distanceMeters) && set.distanceMeters > 0,
  );
  const seconds = sumOf(timed.map((set) => set.durationSeconds));
  const meters = sumOf(timed.map((set) => set.distanceMeters));
  return seconds === null || meters === null ? null : seconds / (meters / 1000);
}

/** One set's figure on a line. A session's is the sum or the best of them, by `SESSION_TOTALS`. */
function setFigure(set: LiftSet, line: LiftLine): number | null {
  switch (line) {
    case 'e1rm':
      return set.e1rmKg;
    case 'weight':
      return set.weightKg;
    case 'reps':
    case 'bestSet':
      return set.reps;
    case 'volume':
      return setVolumeKg(set);
    case 'longestSet':
    case 'totalTime':
    case 'time':
      return set.durationSeconds;
    case 'distance':
      return set.distanceMeters;
    case 'pace':
      return paceOf([set]);
    case 'rounds':
      return set.rounds;
    case 'speed':
      return set.speedKmh;
  }
}

/** The lines a session adds up; every other line is the session's best set. */
const SESSION_TOTALS: ReadonlySet<LiftLine> = new Set([
  'reps',
  'volume',
  'totalTime',
  'time',
  'distance',
  'rounds',
]);

function figureOver(sets: readonly LiftSet[], line: LiftLine): number | null {
  if (line === 'pace') return paceOf(sets);
  const values = sets.map((set) => setFigure(set, line));
  return SESSION_TOTALS.has(line) ? sumOf(values) : maxOf(values);
}

/** Sessions oldest first. Rows without a session or set identity cannot be grouped and are dropped. */
export function toLiftSessions(sources: readonly LiftSetSource[]): readonly LiftSession[] {
  const bySession = new Map<number, { at: number; sets: LiftSet[] }>();
  for (const source of sources) {
    if (source.workoutExecutionId === null || source.setId === null) continue;
    const at = source.achievedAt === null ? Number.NaN : Date.parse(source.achievedAt);
    if (!Number.isFinite(at)) continue;
    const entry = bySession.get(source.workoutExecutionId) ?? { at, sets: [] };
    if (entry.sets.some((set) => set.setId === source.setId)) continue;
    entry.at = Math.min(entry.at, at);
    entry.sets.push({
      setId: source.setId,
      weightKg: source.weightKg,
      reps: source.reps,
      e1rmKg: source.e1rmKg,
      durationSeconds: source.durationSeconds ?? null,
      distanceMeters: source.distanceMeters ?? null,
      rounds: source.rounds ?? null,
      speedKmh: source.speedKmh ?? null,
    });
    bySession.set(source.workoutExecutionId, entry);
  }

  return [...bySession.entries()]
    .map(([workoutExecutionId, entry]): LiftSession => {
      const sets = [...entry.sets].sort((left, right) => left.setId - right.setId);
      return {
        workoutExecutionId,
        performedAt: entry.at,
        sets,
        e1rmKg: figureOver(sets, 'e1rm'),
        topWeightKg: figureOver(sets, 'weight'),
        reps: figureOver(sets, 'reps'),
        volumeKg: figureOver(sets, 'volume'),
      };
    })
    .sort(
      (left, right) =>
        left.performedAt - right.performedAt || left.workoutExecutionId - right.workoutExecutionId,
    );
}

/**
 * The session's figure on one line, in the wire's units: kilograms, seconds, metres, seconds per
 * kilometre for pace. Volume stays in kilograms; the reader chooses the unit.
 */
export function sessionFigure(session: LiftSession, line: LiftLine): number | null {
  return figureOver(session.sets, line);
}

/**
 * **Spadek**: how far the session's LAST counted set fell short of its best, on the chosen line.
 * Zero when the last set was the best one, negative otherwise, `null` when the last set carries no
 * figure on that line. Volume is in kilograms here, like every figure this module hands out.
 */
export function sessionDrop(session: LiftSession, line: LiftLine): number | null {
  const last = session.sets.at(-1);
  if (last === undefined) return null;
  const lastFigure = setFigure(last, line);
  const best = maxOf(session.sets.map((set) => setFigure(set, line)));
  if (!finite(lastFigure) || best === null) return null;
  return lastFigure - best;
}

/** The filmed set a recording is anchored to, as the clip read names it. */
export interface SetRef {
  readonly workoutExecutionId: number;
  readonly setId: number | null;
  /** The logger's own 1-based index, which counts warm-ups too. */
  readonly setNumber: number | null;
}

/**
 * Where a filmed set stands in its session. `counted` places it among the session's counted sets,
 * the same sets its total is taken over. A set the history does not hold (a warm-up, a set left
 * unticked, or a session older than the history) keeps the logger's own number and names no total:
 * the anchor carries no set type, so the absence alone cannot say which of these it is.
 */
export type SetPosition =
  | { readonly kind: 'counted'; readonly number: number; readonly total: number }
  | { readonly kind: 'numbered'; readonly number: number }
  | { readonly kind: 'session' };

export function setPosition(sessions: readonly LiftSession[], ref: SetRef): SetPosition {
  const session = sessions.find((entry) => entry.workoutExecutionId === ref.workoutExecutionId);
  if (session !== undefined && ref.setId !== null) {
    const index = session.sets.findIndex((set) => set.setId === ref.setId);
    if (index !== -1) return { kind: 'counted', number: index + 1, total: session.sets.length };
  }
  return ref.setNumber === null ? { kind: 'session' } : { kind: 'numbered', number: ref.setNumber };
}
